<script setup>
import { t, yearLabel as formatYear } from '../lib/i18n.mjs'
import { computed, ref, onUnmounted } from 'vue'
import AppIcon from './AppIcon.vue'
const props = defineProps({ timeline: Object, range: Array })
const emit = defineEmits(['change'])
const axis = ref(null)
const domain = computed(() => props.timeline.domain || [0, 1])
const value = computed(() => props.range || domain.value)
const percent = (year) =>
  domain.value[0] === domain.value[1]
    ? 50
    : ((year - domain.value[0]) / (domain.value[1] - domain.value[0])) * 100
const ticks = computed(() => [
  ...new Set(
    Array.from({ length: 5 }, (_, i) =>
      Math.round(domain.value[0] + ((domain.value[1] - domain.value[0]) * i) / 4),
    ),
  ),
])
let drag = null
function setYear(index, year) {
  const next = [...value.value]
  next[index] = Math.max(
    index ? next[0] : domain.value[0],
    Math.min(index ? domain.value[1] : next[1], Math.round(year)),
  )
  emit('change', next)
}
function move(event) {
  if (drag === null) return
  const rect = axis.value.getBoundingClientRect()
  const ratio = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
  setYear(drag, domain.value[0] + ratio * (domain.value[1] - domain.value[0]))
}
function stop() {
  drag = null
  window.removeEventListener('pointermove', move)
  window.removeEventListener('pointerup', stop)
  window.removeEventListener('pointercancel', stop)
}
function start(event, index) {
  if (event.button !== 0) return
  event.preventDefault()
  event.currentTarget.focus()
  drag = index
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', stop)
  window.addEventListener('pointercancel', stop)
}
function key(event, index) {
  const actions = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1, PageUp: -10, PageDown: 10 }
  if (event.key === 'Home' || event.key === 'End' || event.key in actions) {
    event.preventDefault()
    setYear(
      index,
      event.key === 'Home'
        ? domain.value[0]
        : event.key === 'End'
          ? domain.value[1]
          : value.value[index] + actions[event.key],
    )
  }
}
onUnmounted(stop)
</script>
<template>
  <div class="time-facet">
    <div class="time-title">
      <span>{{ t('时间') }}</span
      ><button
        v-if="range"
        class="icon-button"
        :title="t('清除时间筛选')"
        :aria-label="t('清除时间筛选')"
        @click="emit('change', null)"
      >
        <AppIcon name="RotateCcw" :size="10" />
      </button>
    </div>
    <template v-if="timeline.domain">
      <div ref="axis" class="time-axis" :aria-label="t('时间轴')">
        <div class="time-track" />
        <div
          class="time-selection"
          :style="{ top: `${percent(value[0])}%`, height: `${percent(value[1]) - percent(value[0])}%` }"
        />
        <div v-for="tick in ticks" :key="tick" class="time-tick" :style="{ top: `${percent(tick)}%` }">
          <i /><span>{{ formatYear(tick) }}</span>
        </div>
        <button
          v-for="index in [0, 1]"
          :key="index"
          class="time-handle"
          :class="index ? 'time-end' : 'time-start'"
          role="slider"
          aria-orientation="vertical"
          :aria-label="index ? t('结束年份') : t('开始年份')"
          :aria-valuemin="index ? value[0] : domain[0]"
          :aria-valuemax="index ? domain[1] : value[1]"
          :aria-valuenow="value[index]"
          :aria-valuetext="formatYear(value[index])"
          :style="{ top: `${percent(value[index])}%` }"
          @pointerdown="start($event, index)"
          @keydown="key($event, index)"
        >
          <span class="time-handle-value">{{ formatYear(value[index]) }}</span>
        </button>
      </div>
      <output v-if="range" class="time-range" aria-live="polite"
        >{{ formatYear(value[0]) }}–{{ formatYear(value[1]) }}</output
      >
      <div class="time-unknown" :title="t('{0} 个论证单元没有可识别的时间', { 0: timeline.undated })">
        {{ t('未定年') }} {{ timeline.undated }}
      </div>
    </template>
    <span v-else class="time-empty">{{ t('暂无时间') }}</span>
  </div>
</template>
